import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, Paper, Stack, Typography } from "@mui/material";
import {
  AdminPanelSettingsRounded,
  AssignmentRounded,
  GroupsRounded,
  ManageAccountsRounded,
  QueryStatsRounded,
  RefreshRounded,
  RouteRounded,
  TrendingUpRounded,
} from "@mui/icons-material";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import {
  ADMIN_COLORS,
  AdminHeader,
  AdminPage,
  LoadingState,
  ROLE_OPTIONS,
  RoleChip,
  SectionCard,
  StatCard,
  getProcessMeta,
  getRoleMeta,
  primaryButtonSx,
  secondaryButtonSx,
} from "../components/admin/AdminUI";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={0} sx={{ p: 1, borderRadius: "12px", border: `1px solid ${ADMIN_COLORS.border}`, boxShadow: ADMIN_COLORS.shadow }}>
      <Typography sx={{ fontWeight: 900, color: ADMIN_COLORS.textStrong, fontSize: ".78rem" }}>{label}</Typography>
      {payload.map((item) => (
        <Typography key={item.name} sx={{ color: item.color || ADMIN_COLORS.primary, fontSize: ".75rem" }}>
          {item.name}: {item.value}
        </Typography>
      ))}
    </Paper>
  );
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [maturityStats, setMaturityStats] = useState([]);
  const [trends, setTrends] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [u, q, p, m, t] = await Promise.all([
        axiosInstance.get("/admin/users"),
        axiosInstance.get("/admin/preguntas"),
        axiosInstance.get("/admin/procesos"),
        axiosInstance.get("/admin/procesos/madurez"),
        axiosInstance.get("/admin/evaluaciones/trends"),
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setQuestions(Array.isArray(q.data) ? q.data : []);
      setProcesses(Array.isArray(p.data) ? p.data : []);
      setMaturityStats(Array.isArray(m.data) ? m.data : []);
      setTrends(Array.isArray(t.data) ? t.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const usersByRole = useMemo(() => {
    const counts = users.reduce((acc, user) => {
      acc[user.role] = (acc[user.role] || 0) + 1;
      return acc;
    }, {});
    return ROLE_OPTIONS.map((role) => ({ name: role.label, value: counts[role.value] || 0, color: role.color, role: role.value })).filter((item) => item.value > 0);
  }, [users]);

  const processesByType = useMemo(() => {
    const counts = processes.reduce((acc, process) => {
      const key = process.tipo || "Sin tipo";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([type, count]) => ({ type, count, ...getProcessMeta(type) }));
  }, [processes]);

  const maturityChart = useMemo(() => {
    return (maturityStats || [])
      .filter((item) => [1, 2, 3, 4, 5].includes(Number(item.nivel)))
      .sort((a, b) => Number(a.nivel) - Number(b.nivel))
      .map((item) => ({ nivel: `Nivel ${item.nivel}`, procesos: item.count || item.total || 0 }));
  }, [maturityStats]);

  const totalEvaluaciones = (trends || []).reduce((sum, item) => sum + Number(item.evaluaciones || 0), 0);

  if (loading) return <LoadingState text="Cargando panel de administración..." />;

  return (
    <AdminPage>
      <AdminHeader
        eyebrow="Panel de administración"
        title="Centro de control DQPA"
        subtitle="Supervisa usuarios, contenidos de evaluación, procesos y actividad de la plataforma desde una vista ejecutiva."
        icon={<AdminPanelSettingsRounded />}
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshRounded />} sx={secondaryButtonSx} onClick={loadData}>Actualizar</Button>
            <Button variant="contained" startIcon={<ManageAccountsRounded />} sx={primaryButtonSx} onClick={() => navigate("/admin-dashboard/usuarios")}>Gestionar usuarios</Button>
          </>
        }
      />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Usuarios" value={users.length} subtitle="Cuentas registradas" icon={<GroupsRounded />} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Preguntas" value={questions.length} subtitle="Banco de evaluación" icon={<AssignmentRounded />} color={ADMIN_COLORS.accent} bg={ADMIN_COLORS.accentSoft} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Procesos" value={processes.length} subtitle="Modelo de procesos" icon={<RouteRounded />} color={ADMIN_COLORS.success} bg={ADMIN_COLORS.successSoft} /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="Evaluaciones" value={totalEvaluaciones} subtitle="Actividad acumulada" icon={<QueryStatsRounded />} color={ADMIN_COLORS.danger} bg={ADMIN_COLORS.dangerSoft} /></Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={5}>
          <SectionCard title="Usuarios por perfil" subtitle="Distribución de roles activos en la plataforma.">
            <Box sx={{ p: 2, height: 330 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={usersByRole} dataKey="value" nameKey="name" innerRadius={62} outerRadius={98} paddingAngle={4}>
                    {usersByRole.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
              {usersByRole.map((item) => (
                <Stack key={item.role} direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.1, borderRadius: "14px", bgcolor: "#F8FAFC" }}>
                  <RoleChip role={item.role} />
                  <Typography sx={{ fontWeight: 950, color: ADMIN_COLORS.textStrong }}>{item.value}</Typography>
                </Stack>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <SectionCard title="Tendencia de evaluaciones" subtitle="Evolución mensual de uso registrada en la plataforma.">
            <Box sx={{ p: 2, height: 430 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends} margin={{ top: 20, right: 20, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,36,56,.08)" />
                  <XAxis dataKey="month" tick={{ fill: ADMIN_COLORS.muted, fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fill: ADMIN_COLORS.muted, fontSize: 12 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="evaluaciones" name="Evaluaciones" stroke={ADMIN_COLORS.primary} strokeWidth={3} dot={{ r: 4, fill: ADMIN_COLORS.primary }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={6}>
          <SectionCard title="Procesos por ámbito" subtitle="Cobertura del modelo por gobierno, gestión y calidad.">
            <Stack spacing={1} sx={{ p: 2 }}>
              {processesByType.map((item) => (
                <Paper key={item.type} elevation={0} sx={{ p: 1.4, borderRadius: "18px", border: `1px solid ${item.color}22`, bgcolor: item.bg }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: item.color }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: ".75rem", color: ADMIN_COLORS.muted }}>{item.count} procesos</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 950, fontSize: "1.45rem", color: item.color }}>{item.count}</Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SectionCard title="Procesos por nivel" subtitle="Distribución del catálogo según nivel del proceso.">
            <Box sx={{ p: 2, height: 310 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={maturityChart} margin={{ top: 16, right: 18, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,36,56,.08)" />
                  <XAxis dataKey="nivel" tick={{ fill: ADMIN_COLORS.muted, fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fill: ADMIN_COLORS.muted, fontSize: 12 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="procesos" name="Procesos" fill={ADMIN_COLORS.accent} radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </AdminPage>
  );
}
